import { addYears, isBefore } from 'date-fns';
import Energy from '@/domain/model/unit/Energy.ts';
import Percentage from '@/domain/model/unit/Percentage.ts';
import Colors from '@/infrastructure/support/Colors.ts';
import { formatWithLocale } from '@/infrastructure/support/utils/DateTimeUtils.ts';

export default class YieldPromiseStatus {
  jsonData;

  constructor(jsonData) {
    this.jsonData = jsonData;
  }

  getJson() {
    return this.jsonData;
  }

  getId() {
    return this.jsonData.id;
  }

  getPlantId() {
    return this.jsonData.plant_id;
  }

  getPeriodIndex() {
    return this.jsonData.period_index;
  }

  /** Summary flags */
  getSummaryFlag() {
    // noinspection JSDeprecatedSymbols
    return this.jsonData.summary;
  }

  isTooEarly() {
    return this.getSummaryFlag() === 'TOO_EARLY';
  }

  isAhead() {
    return this.getSummaryFlag() === 'AHEAD';
  }

  isFarAhead() {
    return this.getSummaryFlag() === 'FAR_AHEAD';
  }

  isBehind() {
    return this.getSummaryFlag() === 'BEHIND';
  }

  isBehindSeverely() {
    return this.getSummaryFlag() === 'BEHIND_SEVERELY';
  }

  isUninitialized() {
    return this.getSummaryFlag() === 'UNINITIALIZED';
  }

  isFinished() {
    return !this.isOngoing();
  }

  getDeliveredTillNow(): Energy {
    return Energy.ofWh(this.jsonData.delivered_till_now_wh);
  }

  getPromisedTillNow(): Energy {
    return Energy.ofWh(this.jsonData.promised_till_now_wh);
  }

  getPromisedTotal(): Energy {
    return Energy.ofWh(this.jsonData.promised_total_wh);
  }

  setPromisedTotal(promisedTotal): void {
    this.jsonData.promised_total_wh = promisedTotal.asWh();
  }

  getCurrentRealizedPct(): Percentage {
    return this.getDeliveredTillNow()
      .divideBy(this.getPromisedTillNow())
      .subtractPercentage(Percentage.of(100));
  }

  getRadiationReceived(): Percentage {
    return Percentage.of(this.jsonData.radiation_received_pct);
  }

  getMissingEnergy(): Energy {
    return this.getPromisedTillNow().subtract(this.getDeliveredTillNow());
  }

  getDateStartString(): string {
    return formatWithLocale(this.getPeriodStart(), 'MMMM do yyyy');
  }

  getPeriodStart(): Date {
    return new Date(this.jsonData.period_start);
  }

  getPeriodStartRaw() {
    return this.jsonData.period_start;
  }

  getPeriodEnd(): Date {
    return addYears(this.getPeriodStart(), 1);
  }

  isOngoing(): boolean {
    return isBefore(new Date(), this.getPeriodEnd());
  }

  getNeededPerformancePctToCompensate(): Percentage {
    const radiationRemaining = 1 - this.getRadiationReceived().asRatio();

    if (radiationRemaining === 0) {
      return this.getMissingEnergy()
        .divideBy(this.getPromisedTotal())
        .addPercentage(Percentage.of(100));
    } else {
      const remainingOfPromise =
        this.getPromisedTotal().times(radiationRemaining);
      const toDeliver = remainingOfPromise.add(this.getMissingEnergy());

      return toDeliver.divideBy(remainingOfPromise);
    }
  }

  getIconColor(): string {
    if (this.isTooEarly()) {
      return Colors.COLOR_NONE;
    } else if (this.isBehind()) {
      return Colors.COLOR_MEDIUM;
    } else if (this.isBehindSeverely()) {
      return Colors.COLOR_NOK;
    } else if (this.isAhead()) {
      return Colors.COLOR_OK;
    } else if (this.isFarAhead()) {
      return Colors.COLOR_GREAT;
    } else {
      return 'black';
    }
  }

  getActiveGapFills() {
    return this.jsonData.gap_fills.filter((gap) => !gap.deleted_at);
  }
}
